// Helper functions for outreach service
// This module contains common utility functions used across modules

import type { DocumentData, QueryDocumentSnapshot } from "firebase/firestore";
import type { MessageTrackingData, EnhancedMessageTrackingData, CampaignStats } from "../types";

/**
 * Helper function to convert Firestore document to typed object
 * This function will be moved from outreachService.ts
 */
export const convertDoc = <T>(doc: QueryDocumentSnapshot<DocumentData>): T => {
  return { id: doc.id, ...doc.data() } as T;
};

// ============================================================================
// BACKWARD COMPATIBILITY LAYER
// ============================================================================

/**
 * Type guard to check if a tracking data object is enhanced
 * @param data - The tracking data object to check
 * @returns True if the object has enhanced fields, false otherwise
 */
export const isEnhancedMessageTrackingData = (
  data: MessageTrackingData | EnhancedMessageTrackingData
): data is EnhancedMessageTrackingData => {
  return 'mailgunMessageId' in data && typeof data.mailgunMessageId === 'string';
};

/**
 * Type guard to check if a tracking data object is basic
 * @param data - The tracking data object to check
 * @returns True if the object is basic MessageTrackingData, false otherwise
 */
export const isBasicMessageTrackingData = (
  data: MessageTrackingData | EnhancedMessageTrackingData
): data is MessageTrackingData => {
  return !isEnhancedMessageTrackingData(data);
};

/**
 * Convert enhanced tracking data to basic tracking data
 * Strips enhanced fields while preserving all basic fields
 * @param enhancedData - The enhanced tracking data to convert
 * @returns Basic MessageTrackingData object
 */
export const toBasicMessageTrackingData = (
  enhancedData: EnhancedMessageTrackingData
): MessageTrackingData => {
  const {
    // Extract enhanced fields to exclude them
    mailgunMessageId,
    deliveredAt,
    bouncedAt,
    complainedAt,
    unsubscribedAt,
    bounceReason,
    userAgent,
    clientInfo,
    geolocation,
    // Keep all basic fields
    ...basicData
  } = enhancedData;

  return basicData;
};

/**
 * Convert basic tracking data to enhanced tracking data with default values
 * Adds enhanced fields with undefined/default values
 * @param basicData - The basic tracking data to convert
 * @param mailgunMessageId - Required Mailgun message ID for enhanced data
 * @returns Enhanced MessageTrackingData object with default enhanced fields
 */
export const toEnhancedMessageTrackingData = (
  basicData: MessageTrackingData,
  mailgunMessageId: string
): EnhancedMessageTrackingData => {
  return {
    ...basicData,
    mailgunMessageId,
    deliveredAt: undefined,
    bouncedAt: undefined,
    complainedAt: undefined,
    unsubscribedAt: undefined,
    bounceReason: undefined,
    userAgent: undefined,
    clientInfo: undefined,
    geolocation: undefined,
  };
};

/**
 * Safely access enhanced fields with fallback values
 * @param data - The tracking data object (basic or enhanced)
 * @param field - The enhanced field to access
 * @param fallback - Fallback value if field doesn't exist or is undefined
 * @returns The field value or fallback
 */
export const safeGetEnhancedField = <T>(
  data: MessageTrackingData | EnhancedMessageTrackingData,
  field: keyof Omit<EnhancedMessageTrackingData, keyof MessageTrackingData>,
  fallback: T
): T => {
  if (isEnhancedMessageTrackingData(data)) {
    const value = data[field];
    return value !== undefined ? (value as T) : fallback;
  }
  return fallback;
};

/**
 * Get Mailgun message ID from tracking data with fallback
 * @param data - The tracking data object
 * @param fallback - Fallback value if mailgunMessageId is not available
 * @returns The Mailgun message ID or fallback
 */
export const getMailgunMessageId = (
  data: MessageTrackingData | EnhancedMessageTrackingData,
  fallback: string = ''
): string => {
  return safeGetEnhancedField(data, 'mailgunMessageId', fallback);
};

/**
 * Get client information from tracking data with fallback
 * @param data - The tracking data object
 * @returns Client info object or undefined if not available
 */
export const getClientInfo = (
  data: MessageTrackingData | EnhancedMessageTrackingData
): { name: string; os: string; device: string } | undefined => {
  return safeGetEnhancedField(data, 'clientInfo', undefined);
};

/**
 * Get geolocation information from tracking data with fallback
 * @param data - The tracking data object
 * @returns Geolocation object or undefined if not available
 */
export const getGeolocation = (
  data: MessageTrackingData | EnhancedMessageTrackingData
): { country: string; region: string; city: string } | undefined => {
  return safeGetEnhancedField(data, 'geolocation', undefined);
};

/**
 * Check if message has bounce information
 * @param data - The tracking data object
 * @returns True if bounce information is available
 */
export const hasBounceInfo = (
  data: MessageTrackingData | EnhancedMessageTrackingData
): boolean => {
  return isEnhancedMessageTrackingData(data) && 
         (!!data.bouncedAt || !!data.bounceReason);
};

/**
 * Check if message has engagement tracking (opens/clicks with enhanced data)
 * @param data - The tracking data object
 * @returns True if enhanced engagement tracking is available
 */
export const hasEnhancedEngagement = (
  data: MessageTrackingData | EnhancedMessageTrackingData
): boolean => {
  return isEnhancedMessageTrackingData(data) && 
         (!!data.userAgent || !!data.clientInfo || !!data.geolocation);
};

/**
 * Merge basic and enhanced tracking data safely
 * If enhanced data is provided, it takes precedence over basic data
 * @param basicData - Basic tracking data
 * @param enhancedData - Enhanced tracking data (optional)
 * @returns Merged tracking data
 */
export const mergeTrackingData = (
  basicData: MessageTrackingData,
  enhancedData?: Partial<EnhancedMessageTrackingData>
): MessageTrackingData | EnhancedMessageTrackingData => {
  if (!enhancedData || !enhancedData.mailgunMessageId) {
    return basicData;
  }

  return {
    ...basicData,
    ...enhancedData,
  } as EnhancedMessageTrackingData;
};

// ============================================================================
// VALIDATION UTILITIES
// ============================================================================

/**
 * Validate Mailgun message ID format
 * @param messageId - The message ID to validate
 * @returns True if the message ID is valid
 */
export const isValidMailgunMessageId = (messageId: string): boolean => {
  if (!messageId || typeof messageId !== 'string') {
    return false;
  }
  // Mailgun message IDs are typically in format: <timestamp.random@domain>
  const mailgunIdPattern = /^<[^@]+@[^>]+>$/;
  return mailgunIdPattern.test(messageId);
};

/**
 * Validate ISO timestamp format
 * @param timestamp - The timestamp to validate
 * @returns True if the timestamp is a valid ISO string
 */
export const isValidTimestamp = (timestamp: string): boolean => {
  if (!timestamp || typeof timestamp !== 'string') {
    return false;
  }
  const date = new Date(timestamp);
  return !isNaN(date.getTime()) && timestamp.includes('T');
};

/**
 * Validate client info object structure
 * @param clientInfo - The client info object to validate
 * @returns True if the client info has valid structure
 */
export const isValidClientInfo = (
  clientInfo: any
): clientInfo is { name: string; os: string; device: string } => {
  return (
    clientInfo &&
    typeof clientInfo === 'object' &&
    typeof clientInfo.name === 'string' &&
    typeof clientInfo.os === 'string' &&
    typeof clientInfo.device === 'string'
  );
};

/**
 * Validate geolocation object structure
 * @param geolocation - The geolocation object to validate
 * @returns True if the geolocation has valid structure
 */
export const isValidGeolocation = (
  geolocation: any
): geolocation is { country: string; region: string; city: string } => {
  return (
    geolocation &&
    typeof geolocation === 'object' &&
    typeof geolocation.country === 'string' &&
    typeof geolocation.region === 'string' &&
    typeof geolocation.city === 'string'
  );
};

/**
 * Sanitize and validate enhanced message tracking data
 * @param data - Raw data from API or other source
 * @returns Sanitized EnhancedMessageTrackingData or null if invalid
 */
export const sanitizeEnhancedMessageTrackingData = (
  data: any
): EnhancedMessageTrackingData | null => {
  if (!data || typeof data !== 'object') {
    return null;
  }

  // Validate required basic fields
  if (!data.id || !data.recipient || !data.subject || !data.status || !data.timestamp) {
    return null;
  }

  // Validate required enhanced field
  if (!data.mailgunMessageId || !isValidMailgunMessageId(data.mailgunMessageId)) {
    return null;
  }

  // Validate timestamps
  if (!isValidTimestamp(data.timestamp)) {
    return null;
  }

  const sanitized: EnhancedMessageTrackingData = {
    id: String(data.id),
    recipient: String(data.recipient),
    subject: String(data.subject),
    status: data.status,
    timestamp: data.timestamp,
    mailgunMessageId: data.mailgunMessageId,
    openedAt: data.openedAt && isValidTimestamp(data.openedAt) ? data.openedAt : undefined,
    clickedAt: data.clickedAt && isValidTimestamp(data.clickedAt) ? data.clickedAt : undefined,
    deliveredAt: data.deliveredAt && isValidTimestamp(data.deliveredAt) ? data.deliveredAt : undefined,
    bouncedAt: data.bouncedAt && isValidTimestamp(data.bouncedAt) ? data.bouncedAt : undefined,
    complainedAt: data.complainedAt && isValidTimestamp(data.complainedAt) ? data.complainedAt : undefined,
    unsubscribedAt: data.unsubscribedAt && isValidTimestamp(data.unsubscribedAt) ? data.unsubscribedAt : undefined,
    bounceReason: data.bounceReason ? String(data.bounceReason) : undefined,
    userAgent: data.userAgent ? String(data.userAgent) : undefined,
    error: data.error ? String(data.error) : undefined,
    clientInfo: isValidClientInfo(data.clientInfo) ? data.clientInfo : undefined,
    geolocation: isValidGeolocation(data.geolocation) ? data.geolocation : undefined,
  };

  return sanitized;
};

/**
 * Calculate campaign statistics from message tracking data
 * @param campaignId - The campaign ID
 * @param messages - Array of message tracking data
 * @returns Calculated CampaignStats object
 */
export const calculateCampaignStats = (
  campaignId: string,
  messages: (MessageTrackingData | EnhancedMessageTrackingData)[]
): CampaignStats => {
  const totalSent = messages.length;
  
  if (totalSent === 0) {
    return {
      campaignId,
      totalSent: 0,
      delivered: 0,
      opened: 0,
      clicked: 0,
      bounced: 0,
      complained: 0,
      unsubscribed: 0,
      deliveryRate: 0,
      openRate: 0,
      clickRate: 0,
      bounceRate: 0,
      complaintRate: 0,
    };
  }

  let delivered = 0;
  let opened = 0;
  let clicked = 0;
  let bounced = 0;
  let complained = 0;
  let unsubscribed = 0;

  messages.forEach((message) => {
    // Count delivered messages
    if (message.status === 'delivered' || message.status === 'opened' || message.status === 'clicked') {
      delivered++;
    }

    // Count opened messages
    if (message.status === 'opened' || message.status === 'clicked' || message.openedAt) {
      opened++;
    }

    // Count clicked messages
    if (message.status === 'clicked' || message.clickedAt) {
      clicked++;
    }

    // Count bounced messages (from basic status or enhanced data)
    if (message.status === 'failed' || message.error) {
      bounced++;
    }
    if (isEnhancedMessageTrackingData(message) && message.bouncedAt) {
      bounced++;
    }

    // Count complained messages (enhanced data only)
    if (isEnhancedMessageTrackingData(message) && message.complainedAt) {
      complained++;
    }

    // Count unsubscribed messages (enhanced data only)
    if (isEnhancedMessageTrackingData(message) && message.unsubscribedAt) {
      unsubscribed++;
    }
  });

  // Calculate rates as percentages
  const deliveryRate = totalSent > 0 ? (delivered / totalSent) * 100 : 0;
  const openRate = delivered > 0 ? (opened / delivered) * 100 : 0;
  const clickRate = delivered > 0 ? (clicked / delivered) * 100 : 0;
  const bounceRate = totalSent > 0 ? (bounced / totalSent) * 100 : 0;
  const complaintRate = delivered > 0 ? (complained / delivered) * 100 : 0;

  return {
    campaignId,
    totalSent,
    delivered,
    opened,
    clicked,
    bounced,
    complained,
    unsubscribed,
    deliveryRate: Math.round(deliveryRate * 100) / 100, // Round to 2 decimal places
    openRate: Math.round(openRate * 100) / 100,
    clickRate: Math.round(clickRate * 100) / 100,
    bounceRate: Math.round(bounceRate * 100) / 100,
    complaintRate: Math.round(complaintRate * 100) / 100,
  };
};

/**
 * Validate CampaignStats object structure
 * @param stats - The campaign stats object to validate
 * @returns True if the stats object is valid
 */
export const isValidCampaignStats = (stats: any): stats is CampaignStats => {
  return (
    stats &&
    typeof stats === 'object' &&
    typeof stats.campaignId === 'string' &&
    typeof stats.totalSent === 'number' &&
    typeof stats.delivered === 'number' &&
    typeof stats.opened === 'number' &&
    typeof stats.clicked === 'number' &&
    typeof stats.bounced === 'number' &&
    typeof stats.complained === 'number' &&
    typeof stats.unsubscribed === 'number' &&
    typeof stats.deliveryRate === 'number' &&
    typeof stats.openRate === 'number' &&
    typeof stats.clickRate === 'number' &&
    typeof stats.bounceRate === 'number' &&
    typeof stats.complaintRate === 'number' &&
    stats.totalSent >= 0 &&
    stats.delivered >= 0 &&
    stats.opened >= 0 &&
    stats.clicked >= 0 &&
    stats.bounced >= 0 &&
    stats.complained >= 0 &&
    stats.unsubscribed >= 0
  );
};

/**
 * Sanitize string input by removing potentially harmful characters
 * @param input - The string to sanitize
 * @param maxLength - Maximum allowed length (default: 1000)
 * @returns Sanitized string
 */
export const sanitizeString = (input: string, maxLength: number = 1000): string => {
  if (!input || typeof input !== 'string') {
    return '';
  }
  
  return input
    .trim()
    .slice(0, maxLength)
    .replace(/[<>]/g, '') // Remove potential HTML tags
    .replace(/[\x00-\x1F\x7F]/g, ''); // Remove control characters
};

/**
 * Validate email address format
 * @param email - The email address to validate
 * @returns True if the email format is valid
 */
export const isValidEmail = (email: string): boolean => {
  if (!email || typeof email !== 'string') {
    return false;
  }
  
  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailPattern.test(email) && email.length <= 254;
};